import React from "react"
import PropTypes from "prop-types"
import { Box, Grid, Typography } from "@mui/material"
import { useTheme } from "@mui/material/styles"
import InfoItem from "../InfoCard/Info-item"

const InfoDataPanel = ({
    title,
    data,
    leftItems = [],
    sections = [],
    bottomItems = [],
}) => {
    const theme = useTheme()

    const getValue = (item) => {
        const rawValue = data?.[item.key]

        if (item.formatter) {
            return item.formatter(rawValue, data)
        }

        return rawValue ?? "-"
    }

    const getOptionalText = (item) => {
        if (item.optionalTextFormatter) {
            return item.optionalTextFormatter(data)
        }

        return item.optionalText
    }

    const getOptionalTextLabel = (item) => {
        return item.optionalTextLabel
    }

    const renderGridSection = (section, sectionIndex) => {
        return (
            <Grid
                key={sectionIndex}
                container
                pt={3}
                pb={3}
                pl={3}
                sx={{
                    display: "flex",
                    flexDirection: {
                        xxxs: "column",
                        xs: "row",
                    },
                    background: `linear-gradient(90.8deg, ${theme.palette.transparent.white10} 0%, transparent 100%)`,
                    ...section.sx,
                }}
            >
                {section.items.map((item, itemIndex) => (
                    <Grid
                        key={item.key || `placeholder-${itemIndex}`}
                        item
                        xxxs={2}
                        md={3}
                        lg
                        sx={{
                            paddingBottom: {
                                xxxs: item.placeholder ? 0 : 3,
                                md: 0,
                            },
                            display: item.placeholder
                                ? { xxxs: "none", md: "block" }
                                : "block",
                            ...item.sx,
                        }}
                        {...item.gridProps}
                    >
                        {!item.placeholder && (
                            <InfoItem
                                label={item.label}
                                value={getValue(item)}
                                optionalText={getOptionalText(item)}
                                optionalTextLabel={getOptionalTextLabel(item)}
                                optionalTextOpacity={item.optionalTextOpacity}
                                optionalTextVariant={item.optionalTextVariant}
                            />
                        )}
                    </Grid>
                ))}
            </Grid>
        )
    }

    const renderDividedSection = (section, sectionIndex) => {
        const isFourItems = section.items.length === 4

        return (
            <Box
                key={sectionIndex}
                sx={{
                    display: "flex",
                    flexDirection: "row",
                    flexWrap: { xxxs: "wrap", md: "nowrap" },
                    width: "100%",
                    background: theme.palette.background.secondary,
                    ...section.sx,
                }}
            >
                {section.items.map((item, itemIndex) => (
                    <Box
                        key={item.key || `divided-item-${itemIndex}`}
                        sx={{
                            flex: {
                                xxxs: isFourItems ? "0 0 50%" : "0 0 100%",
                                md: 1,
                            },
                            width: {
                                xxxs: isFourItems ? "50%" : "100%",
                                md: "auto",
                            },
                            boxSizing: "border-box",
                            p: 3,
                            minHeight: 140,
                            display: "flex",
                            alignItems: "flex-start",
                            background: `linear-gradient(146.22deg, ${theme.palette.transparent.white10} 0%, transparent 100%)`,
                            borderLeft:
                                itemIndex === 0
                                    ? "none"
                                    : {
                                          xxxs: "none",
                                          md: `1px solid ${theme.palette.background.primary}`,
                                      },
                            borderTop:
                                isFourItems && itemIndex >= 2
                                    ? {
                                          xxxs: `1px solid ${theme.palette.background.primary}`,
                                          md: "none",
                                      }
                                    : "none",
                            ...item.sx,
                        }}
                    >
                        <InfoItem
                            label={item.label}
                            value={getValue(item)}
                            optionalText={getOptionalText(item)}
                            optionalTextLabel={getOptionalTextLabel(item)}
                            optionalTextOpacity={item.optionalTextOpacity}
                            optionalTextVariant={item.optionalTextVariant}
                        />
                    </Box>
                ))}
            </Box>
        )
    }

    return (
        <Box display="flex" flexDirection="column">
            {title && (
                <Typography variant="h2" color="common.white" sx={{ mb: 3 }}>
                    {title}
                </Typography>
            )}

            <Box
                sx={{
                    display: "flex",
                    flexDirection: { md: "row", xxxs: "column" },
                    gap: 0.125,
                }}
            >
                {leftItems.length > 0 && (
                    <Box
                        p={3}
                        sx={{
                            width: {
                                xxxs: "100%",
                                md: "180px",
                                lg: "300px",
                            },
                            flexShrink: 0,
                            background: `linear-gradient(146.22deg, ${theme.palette.transparent.white10} 0%, transparent 100%)`,
                        }}
                    >
                        <Grid
                            container
                            spacing={2}
                            columns={{
                                xxxs: 4,
                                xs: 4,
                                md: 12,
                                lg: 12,
                            }}
                            sx={{
                                display: "flex",
                                flexDirection: {
                                    xxxs: "row",
                                    md: "column",
                                    lg: "row",
                                },
                            }}
                        >
                            {leftItems.map((item) => (
                                <Grid
                                    key={item.key}
                                    item
                                    xxxs={2}
                                    xs={2}
                                    md={12}
                                    lg={12}
                                    {...item.gridProps}
                                >
                                    <InfoItem
                                        label={item.label}
                                        value={getValue(item)}
                                        optionalText={getOptionalText(item)}
                                        optionalTextLabel={getOptionalTextLabel(
                                            item
                                        )}
                                        optionalTextOpacity={
                                            item.optionalTextOpacity
                                        }
                                        optionalTextVariant={
                                            item.optionalTextVariant
                                        }
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                )}

                <Box
                    display="flex"
                    flexDirection="column"
                    gap={0.125}
                    width="100%"
                >
                    {sections.map((section, sectionIndex) => {
                        if (section.variant === "divided") {
                            return renderDividedSection(section, sectionIndex)
                        }

                        return renderGridSection(section, sectionIndex)
                    })}
                </Box>
            </Box>

            {bottomItems.length > 0 && (
                <Grid
                    container
                    pt={3}
                    pb={3}
                    pl={3}
                    sx={{
                        display: "flex",
                        flexDirection: {
                            xxxs: "column",
                            xs: "row",
                        },
                        background: `linear-gradient(90.8deg, ${theme.palette.transparent.white10} 0%, transparent 100%)`,
                    }}
                >
                    {bottomItems.map((item) => (
                        <Grid
                            key={item.key}
                            item
                            xxxs={4}
                            md={12}
                            sx={{
                                ...item.sx,
                            }}
                            {...item.gridProps}
                        >
                            <InfoItem
                                label={item.label}
                                value={getValue(item)}
                                optionalText={getOptionalText(item)}
                                optionalTextLabel={getOptionalTextLabel(item)}
                                optionalTextOpacity={item.optionalTextOpacity}
                                optionalTextVariant={item.optionalTextVariant}
                            />
                        </Grid>
                    ))}
                </Grid>
            )}
        </Box>
    )
}

InfoDataPanel.propTypes = {
    title: PropTypes.string,
    data: PropTypes.object,
    leftItems: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
            formatter: PropTypes.func,
            optionalText: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.number,
                PropTypes.node,
            ]),
            optionalTextFormatter: PropTypes.func,
            optionalTextLabel: PropTypes.string,
            optionalTextOpacity: PropTypes.number,
            optionalTextVariant: PropTypes.string,
            gridProps: PropTypes.object,
            sx: PropTypes.object,
        })
    ),
    sections: PropTypes.arrayOf(
        PropTypes.shape({
            variant: PropTypes.oneOf(["grid", "divided"]),
            sx: PropTypes.object,
            items: PropTypes.arrayOf(
                PropTypes.shape({
                    key: PropTypes.string,
                    label: PropTypes.string,
                    formatter: PropTypes.func,
                    optionalText: PropTypes.oneOfType([
                        PropTypes.string,
                        PropTypes.number,
                        PropTypes.node,
                    ]),
                    optionalTextFormatter: PropTypes.func,
                    optionalTextLabel: PropTypes.string,
                    optionalTextOpacity: PropTypes.number,
                    optionalTextVariant: PropTypes.string,
                    gridProps: PropTypes.object,
                    sx: PropTypes.object,
                    placeholder: PropTypes.bool,
                })
            ).isRequired,
        })
    ),
    bottomItems: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
            formatter: PropTypes.func,
            optionalText: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.number,
                PropTypes.node,
            ]),
            optionalTextFormatter: PropTypes.func,
            optionalTextLabel: PropTypes.string,
            optionalTextOpacity: PropTypes.number,
            optionalTextVariant: PropTypes.string,
            gridProps: PropTypes.object,
            sx: PropTypes.object,
        })
    ),
}

export default InfoDataPanel